/**
 * MarkdownDoc — the portal's read-only long-form document surface.
 *
 * Renders Markdown as formatted HTML (see utils/markdownDoc.js) with the
 * portal's dark document styling. Used by the Guides Library reader, the
 * Policy Reader and the SOP page so all three read identically.
 *
 * Output is sanitised with DOMPurify on top of the renderer's
 * escape-by-construction behaviour.
 */

import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import { renderMarkdownDoc } from "../utils/markdownDoc";

// 27 Oct 2026 refresh: headings, links and code in indigo, not the old teal
// green, which now means status only.
export const MARKDOWN_DOC_CSS = `
.md-doc { color: #C0D0D0; font-size: 14px; line-height: 1.7; overflow-wrap: break-word; }
.md-doc > :first-child { margin-top: 0; }
.md-doc h1 { color: #a5b4fc; font-size: 1.5rem; font-weight: 700; margin: 2rem 0 0.75rem; padding-bottom: 0.5rem; border-bottom: 1px solid #2a3358; }
.md-doc h2 { color: #a5b4fc; font-size: 1.2rem; font-weight: 600; margin: 1.5rem 0 0.5rem; }
.md-doc h3 { color: #b4bdd6; font-size: 1rem; font-weight: 600; margin: 1.25rem 0 0.4rem; }
.md-doc h4, .md-doc h5, .md-doc h6 { color: #b4bdd6; font-size: 0.9375rem; font-weight: 600; margin: 1rem 0 0.35rem; }
.md-doc p { margin: 0.5rem 0; }
.md-doc strong { color: #F0EDE6; font-weight: 600; }
.md-doc em { color: #b4bdd6; font-style: italic; }
.md-doc del { color: #7a86a8; }
.md-doc a { color: #a5b4fc; text-decoration: underline; }
.md-doc a:hover { color: #c7d2fe; }
.md-doc ul, .md-doc ol { padding-left: 1.5rem; margin: 0.5rem 0; }
.md-doc li { margin: 0.25rem 0; }
.md-doc ul li { list-style-type: disc; }
.md-doc ul ul li { list-style-type: circle; }
.md-doc ol li { list-style-type: decimal; }
.md-doc li > ul, .md-doc li > ol { margin: 0.25rem 0; }
.md-doc table { width: 100%; border-collapse: collapse; margin: 0.75rem 0; font-size: 13px; display: table; }
.md-doc th { background: rgba(99, 102, 241, 0.18); color: #c7d2fe; font-weight: 600; text-align: left; padding: 0.5rem 0.75rem; border: 1px solid #2a3358; vertical-align: top; }
.md-doc td { padding: 0.5rem 0.75rem; border: 1px solid #2a3358; color: #C0D0D0; vertical-align: top; }
.md-doc tbody tr:nth-child(even) td { background: #0f1730; }
.md-doc blockquote { border-left: 3px solid #4f46e5; padding: 0.5rem 1rem; margin: 0.75rem 0; background: rgba(99, 102, 241, 0.08); color: #b4bdd6; }
.md-doc blockquote p { margin: 0.25rem 0; }
.md-doc code { color: #c4b5fd; background: #0f1730; padding: 0.125rem 0.375rem; border-radius: 4px; font-size: 13px; }
.md-doc pre { background: #0f1730; border: 1px solid #2a3358; border-radius: 6px; padding: 0.75rem 1rem; margin: 0.75rem 0; overflow-x: auto; }
.md-doc pre code { background: none; padding: 0; color: #C0D0D0; }
.md-doc hr { border: 0; border-top: 1px solid #2a3358; margin: 1.5rem 0; }
.md-doc img { max-width: 100%; height: auto; }
`;

export default function MarkdownDoc({ markdown, className = "", testId }) {
  const html = useMemo(
    () => DOMPurify.sanitize(renderMarkdownDoc(markdown), { ADD_ATTR: ["target", "rel"] }),
    [markdown]
  );

  return (
    <>
      <style>{MARKDOWN_DOC_CSS}</style>
      <div
        className={`md-doc ${className}`.trim()}
        data-testid={testId}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </>
  );
}
